<template>
    <div class="flex items-start relative overflow-hidden">
        <button
            type="button"
            class="btn-icon btn-white absolute top-1 right-1 z-40"
            @click="store.setCourseSidebarOpen()"
            id="hs-course-show-sidebar"
        >
            <Icon icon="list" v-if="!courseSidebarOpen" />
            <Icon icon="close" v-else />
        </button>

        <div class="flex-1 lg:pr-0 flex flex-col justify-start">
            <div class="relative">
                <div
                    class="flex-1 max-h-[calc(100vh-225px)] md:max-h-[calc(100vh-380px)] lg:max-h-[calc(100vh-300px)] lg:min-h-[calc(100vh-300px)] overflow-y-auto pt-4 pb-4 md:pb-16"
                >
                    <div class="w-full lg:max-w-2xl mx-auto">
                        <h3
                            class="text-slate-800 dark:text-slate-100 text-2xl lg:text-3xl xl:text-4xl font-bold mb-8"
                        >
                            {{ chapter?.title }}
                        </h3>
                        <div
                            class="prose prose-courses dark:prose-invert"
                            v-safe-html="chapter?.description"
                        ></div>
                        <div
                            class="absolute bottom-10 left-0 w-full h-28 bg-gradient-to-b from-slate-100/0 via-slate-100/70 via-50% to-slate-100 dark:from-slate-900/0 dark:via-slate-900/70 dark:to-slate-900 z-20 pointer-events-none"
                        ></div>
                    </div>
                </div>
                <!-- start button container: -->
                <div
                    class="w-full max-w-2xl mx-auto flex items-center gap-4 md:justify-end justify-center pt-1 pr-2 md:pt-2 md:pr-4"
                >
                    <Link
                        :href="`/course/chapters/${previousChapter.slug}`"
                        class="btn-base btn-white"
                        v-if="previousChapter"
                        id="hs-course-previous-chapter"
                        :disabled="loadingCourse"
                    >
                        <Icon icon="arrow-right" class="rotate-180" />
                        Previous
                    </Link>
                    <button
                        type="button"
                        class="btn-base btn-10x-orange"
                        @click="completeChapter()"
                        v-if="!chapter?.completed && nextChapter"
                        id="hs-course-complete-chapter"
                    >
                        Complete and continue
                        <Icon icon="arrow-right" />
                    </button>
                    <button
                        type="button"
                        class="btn-base btn-10x-orange"
                        @click="completeCourse()"
                        :disabled="loadingCourse"
                        v-if="
                            !chapter?.completed &&
                            currentChapter &&
                            !nextChapter
                        "
                        id="hs-course-complete-course"
                    >
                        Complete Course
                        <Icon icon="arrow-right" />
                    </button>
                </div>
                <!-- end button container -->
                <div class="" v-if="loadingCourse">
                    <LoadingState :loading="loadingCourse" />
                </div>
            </div>
        </div>

        <!-- start navigation aside:  -->
        <aside
            class="bg-slate-100 dark:bg-slate-900 transition-all max-h-[calc(100vh-320px)] lg:max-h-[calc(100vh-280px)] overflow-y-auto overflow-hidden"
            :class="{
                'translate-x-full w-0': !courseSidebarOpen,
                'translate-x-0 w-full absolute right-0 top-4 z-20 lg:static lg:top-auto lg:right-auto lg:w-[360px]':
                    courseSidebarOpen,
            }"
            ref="courseSidebar"
        >
            <LoadingState
                :loading="loadingCourse"
                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
            />
            <CourseSidebar />
        </aside>
    </div>
</template>
<script setup>
import { router, usePage, Link } from '@inertiajs/vue3';

import { useCoursesStore } from '@/store/courses';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import CourseSidebar from '@/components/courses/course-sidebar/course-sidebar.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useCourseChatbot } from '@/composables/useCourseChatbot';
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';

const store = useCoursesStore();
const appStore = useAppStore();
const page = usePage();
const { showChatbot } = useCourseChatbot();

const { course, chapters, loadingCourse, courseSidebarOpen, currentChapter } =
    storeToRefs(store);

const chapter = computed(() => page.props.chapter?.data);

const nextChapter = computed(() => {
    const chapterIndex = chapters.value.findIndex(
        (chpt) => chpt?.uuid === chapter?.value?.uuid
    );

    if (chapters.value[chapterIndex + 1]) {
        return chapters.value[chapterIndex + 1];
    }

    return null;
});

const previousChapter = computed(() => {
    const chapterIndex = chapters.value.findIndex(
        (chpt) => chpt?.uuid === chapter?.value?.uuid
    );
    return chapters.value[chapterIndex - 1];
});

// The visit marks the chapter complete and redirects to the next one, so it
// is the only request; a separate API call completed every chapter twice.
// The sidebar only shows it complete once the server has saved it.
const completeChapter = () => {
    const chapterUuid = chapter.value.uuid;

    router.get(`/api/course/chapters/${chapterUuid}/complete`, {}, {
        onSuccess: () => store.setChapterStatus(chapterUuid, 'complete'),
    });
};

const completeCourse = () => {
    store.completeChapter(chapter.value.uuid);
    // The course may not have loaded (e.g. its request was rate limited);
    // /course shows the same page (MY10X-236).
    router.visit(course.value?.slug ? `/course/${course.value.slug}` : '/course');
};

const courseSidebar = ref(null);

const scrollToChapter = () => {
    if (!chapter.value || !chapter.value.slug) {
        return;
    }

    let offsetPosition = 0;
    let elementPosition = 0;
    const chapterId = `chapter-${chapter.value.slug}`;
    const chapterElement = document.getElementById(chapterId);

    if (!chapterElement) {
        return;
    }

    const headerOffset = 280;
    elementPosition = chapterElement.getBoundingClientRect().top;
    offsetPosition = elementPosition - headerOffset;

    if (offsetPosition > 0) {
        courseSidebar.value?.scrollTo({
            top: offsetPosition,
        });
    }
};

onMounted(() => {
    showChatbot();

    if (!course.value) {
        store.getFirstCourse();
    }

    if (chapter.value) {
        setTimeout(() => {
            scrollToChapter();
        }, 300);
    }

    if (page.props.chapterSlug) {
        if (page.props.chapter?.data.user_status === 'new') {
            store.startChapter(page.props.chapter?.data.uuid);
        }
        appStore.setBreadcrumbs('chapterSlug', page.props.chapter?.data.title);
        store.setCurrentChapter(page.props.chapter?.data);
    }
});
</script>
